/// <reference path="../../../types/axios-fix.d.ts" />
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { config } from '../../../config/runtime-config';
import connectionStatusService from '../connectionStatusService';
import { logError } from '../../utils/error-handler';

/**
 * Core HTTP Client Service
 * 
 * Provides a configured axios instance matching the existing API service patterns.
 * Handles base URL configuration, timeouts, and basic headers.
 */
export class HttpClientService {
  private instance: AxiosInstance;
  private readonly baseURL: string;
  private readonly DEFAULT_TIMEOUT = 10000; // 10s for normal operations
  private readonly LONG_RUNNING_TIMEOUT = 60000; // 60s for Stripe/long operations

  constructor() {
    // Use the runtime config for API URL
    this.baseURL = config.apiUrl;

    this.instance = axios.create({
      baseURL: this.baseURL,
      timeout: this.DEFAULT_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    }) as any; // Type assertion to work around AxiosInstance type issues

    // Add request interceptor to log outgoing requests (for debugging)
    this.instance.interceptors.request.use(
      (config) => {
        if (config.method?.toUpperCase() === 'PUT' || config.method?.toUpperCase() === 'POST' || config.method?.toUpperCase() === 'PATCH') {
          console.log(`HTTP ${config.method?.toUpperCase()} Request:`, {
            url: config.url,
            data: config.data,
            headers: config.headers
          });
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add global error interceptor to handle network errors gracefully
    this.instance.interceptors.response.use(
      (response) => {
        // If we get a successful response and were previously disconnected, mark as connected
        if (connectionStatusService.getStatus() === 'disconnected') {
          connectionStatusService.setStatus('connected');
        }
        return response;
      },
      (error) => {
        // Check if it's a network or timeout error
        if (!error.response) {
          // Request timeout (operation took too long)
          if (error.code === 'ECONNABORTED') {
            logError('HTTP Client Timeout', error);

            const timeoutError = new Error(
              'The operation is taking longer than expected. It may still complete successfully. ' +
              'Please wait a moment and check if your changes were applied.'
            );
            (timeoutError as any).code = 'TIMEOUT_ERROR';
            (timeoutError as any).isTimeout = true;
            (timeoutError as any).originalError = error;

            return Promise.reject(timeoutError);
          }

          // Actual network error (can't reach server)
          if (error.message === 'Network Error') {
            logError('HTTP Client Network Error', error);

            // Update connection status to disconnected
            connectionStatusService.setStatus('disconnected');

            const networkError = new Error('Unable to connect to the server. Please check your connection and try again.');
            (networkError as any).code = 'NETWORK_ERROR';
            (networkError as any).isNetworkError = true;
            (networkError as any).originalError = error;

            return Promise.reject(networkError);
          }
        }

        // For other errors, pass them through
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get the axios instance for direct use
   */
  getInstance(): AxiosInstance {
    return this.instance;
  }

  /**
   * Get base URL
   */
  getBaseURL(): string {
    return this.baseURL;
  }

  /**
   * Get health endpoint URL
   * Replaces /api/v2 with /api/health for health checks
   */
  getHealthURL(): string {
    return this.baseURL.replace('/api/v2', '/api/health');
  }

  /**
   * Add request interceptor
   */
  addRequestInterceptor(
    onFulfilled?: (config: AxiosRequestConfig) => AxiosRequestConfig | Promise<AxiosRequestConfig>,
    onRejected?: (error: any) => any
  ): number {
    return this.instance.interceptors.request.use(onFulfilled, onRejected);
  }

  /**
   * Add response interceptor
   */
  addResponseInterceptor(
    onFulfilled?: (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>,
    onRejected?: (error: any) => any
  ): number {
    return this.instance.interceptors.response.use(onFulfilled, onRejected);
  }

  /**
   * Remove request interceptor
   */
  removeRequestInterceptor(id: number): void {
    this.instance.interceptors.request.eject(id);
  }

  /**
   * Remove response interceptor
   */
  removeResponseInterceptor(id: number): void {
    this.instance.interceptors.response.eject(id);
  }

  /**
   * Make HTTP requests - delegate to axios instance
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, data, config);
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.patch<T>(url, data, config);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url, config);
  }

  async head<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.head<T>(url, config);
  }

  async options<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.options<T>(url, config);
  }

  async request<T = any>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.request<T>(config);
  }
}

// Export singleton instance
export const httpClientService = new HttpClientService();